import { useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  height?: string;
}

const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = "Escribe tu contenido aquí...", 
  label,
  className,
  height = "200px"
}: RichTextEditorProps) => {
  const quillRef = useRef<ReactQuill>(null);

  // Handle image upload
  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        try {
          // Generate unique filename
          const fileExt = file.name.split('.').pop();
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

          // Upload to Supabase Storage
          const { data, error } = await supabase.storage
            .from('images_2025_11_10_14_11')
            .upload(fileName, file);

          if (error) throw error;

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('images_2025_11_10_14_11')
            .getPublicUrl(data.path);

          // Insert image into editor
          const quill = quillRef.current?.getEditor();
          if (quill) {
            const range = quill.getSelection();
            quill.insertEmbed(range?.index || 0, 'image', publicUrl);
          }

          toast({
            title: "Imagen subida",
            description: "La imagen se ha insertado correctamente",
          });
        } catch (error: any) {
          toast({
            title: "Error al subir imagen",
            description: error.message,
            variant: "destructive",
          });
        }
      }
    };
  };
  
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }, { 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchVisual: false,
    }
  }), []);

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet',
    'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  return (
    <div className={className}>
      {label && <Label className="text-sm font-medium mb-2 block">{label}</Label>}
      <div className="rich-text-editor">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          modules={modules}
          formats={formats}
          style={{
            height: height,
            marginBottom: '42px' // Space for toolbar
          }}
        />
      </div>
      
      <style jsx global>{`
        .rich-text-editor .ql-editor {
          min-height: ${height};
          font-family: inherit;
          font-size: 14px;
          line-height: 1.6;
        }
        
        .rich-text-editor .ql-toolbar {
          border-top: 1px solid hsl(var(--border));
          border-left: 1px solid hsl(var(--border));
          border-right: 1px solid hsl(var(--border));
          border-radius: 6px 6px 0 0;
          background: hsl(var(--background));
        }
        
        .rich-text-editor .ql-container {
          border-bottom: 1px solid hsl(var(--border));
          border-left: 1px solid hsl(var(--border));
          border-right: 1px solid hsl(var(--border));
          border-radius: 0 0 6px 6px;
          background: hsl(var(--background));
        }
        
        .rich-text-editor .ql-editor.ql-blank::before {
          color: hsl(var(--muted-foreground));
          font-style: normal;
        }
        
        .rich-text-editor .ql-snow .ql-picker {
          color: hsl(var(--foreground));
        }
        
        .rich-text-editor .ql-snow .ql-stroke {
          stroke: hsl(var(--foreground));
        }
        
        .rich-text-editor .ql-snow .ql-fill {
          fill: hsl(var(--foreground));
        }
        
        .rich-text-editor .ql-snow .ql-picker-options {
          background: hsl(var(--background));
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        }
        
        .rich-text-editor .ql-snow .ql-picker-item:hover {
          background: hsl(var(--accent));
        }
        
        .rich-text-editor .ql-snow .ql-tooltip {
          background: hsl(var(--background));
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
          color: hsl(var(--foreground));
        }
        
        .rich-text-editor .ql-snow .ql-tooltip input {
          background: hsl(var(--background));
          border: 1px solid hsl(var(--border));
          border-radius: 4px;
          color: hsl(var(--foreground));
          padding: 4px 8px;
        }
        
        .rich-text-editor .ql-snow .ql-tooltip a {
          color: hsl(var(--primary));
        }
        
        .rich-text-editor .ql-snow .ql-tooltip a:hover {
          color: hsl(var(--primary));
          opacity: 0.8;
        }
        
        /* Custom button styles */
        .rich-text-editor .ql-snow .ql-toolbar button:hover {
          background: hsl(var(--accent));
          border-radius: 4px;
        }
        
        .rich-text-editor .ql-snow .ql-toolbar button.ql-active {
          background: hsl(var(--primary));
          color: hsl(var(--primary-foreground));
          border-radius: 4px;
        }
        
        /* Blockquote styling */
        .rich-text-editor .ql-editor blockquote {
          border-left: 4px solid hsl(var(--primary));
          padding-left: 16px;
          margin-left: 0;
          margin-right: 0;
          font-style: italic;
          background: hsl(var(--muted) / 0.3);
          padding: 12px 16px;
          border-radius: 0 6px 6px 0;
        }
        
        /* Code block styling */
        .rich-text-editor .ql-editor pre.ql-syntax {
          background: hsl(var(--muted));
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          padding: 12px;
          overflow-x: auto;
        }
        
        /* Link styling */
        .rich-text-editor .ql-editor a {
          color: hsl(var(--primary));
          text-decoration: underline;
        }
        
        .rich-text-editor .ql-editor a:hover {
          opacity: 0.8;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;